import { useEffect, useMemo, useState } from 'react'
import Auth from '@aws-amplify/auth'

import { Header } from 'components/Header'
import { FooterPaymentQRM } from './Footer'
import { RenderIf } from 'components/RenderIf'
import { errorHandler } from 'components/ErrorHandler'
import { Spinner } from 'featureSliced/entities/Spinner'

import { Cart } from 'featureSliced/widgets/Cart'
import { Tips } from 'featureSliced/widgets/Tips'
import { SubFooter } from 'featureSliced/widgets/Cart/SubFooter'
import { AmountHandler } from 'featureSliced/widgets/AmountHandler'
import { AgeProtectionModal } from 'featureSliced/widgets/AgeProtectionModal'

import {
  ITableCloudItem,
  ITableCloudItemGroupped,
  TableStatuses,
} from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

import { CardsModal } from './QRMCardsModal'

import { CurrentPageType } from './types'
import { EmptyBlock, PaymentContainer } from './styled'
import { FakeCart } from '../../widgets/Cart/FakeCart'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ConfirmationModal } from 'featureSliced/features/ConfirmationModal'
import { useOpenCallWaiterModal } from 'featureSliced/shared/hooks/openModals/useOpenCallWaiterModal'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { ICartProduct } from 'featureSliced/shared/store/generalTypes'
import { getProductTotalPrice } from 'featureSliced/shared/utils/getProductTotalPrice'
import { clickCollectGET } from 'featureSliced/shared/api/clickCollect/GET'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { usePayment } from 'featureSliced/shared/hooks/usePayment'
import { useProductProtection } from 'featureSliced/shared/hooks/useProductProtection'

type SplitProductType = {
  uniqueId: string
  count: number
}

export const PaymentQRM = () => {
  // Hooks
  const t = useTranslations()
  const navigate = useNavigate()
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()
  const [searchParams] = useSearchParams()
  const { startQRMAfterMealPayment: startPayment } = usePayment()
  const openCallWaiterModal = useOpenCallWaiterModal()
  const { isProductHighlyProtected, isProductProtected } =
    useProductProtection()

  // Store
  const isUserAuth = useMySelector((state) => state.auth.isLoggedIn)
  const currency = useMySelector((state) => state.app.companyData.Currency)
  const paymentVariant = useMySelector((state) => state.app.menu.paymentVariant)
  const isProductsLoaded = useMySelector((state) => state.categories.isLoaded)
  const disabledByMW = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )
  const {
    tableStatus,
    cloudItems,
    isTableLoading,
    isTableDataLoaded,
    modVer,
    orderGuid,
    cloudTotal,
  } = useMySelector((state) => state.qrMenuTable)
  const openToast = ToastsSlice.actions.openToast

  // State
  const [currentPage, setCurrentPage] = useState<CurrentPageType>('cart')
  const [isAVGConfirmed, setIsAVGConfirmed] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tipsValue, setTipsValue] = useState<number>(0)
  const [loyaltyCardBalance, setLoyaltyCardBalance] = useState<number>(0)

  // Split state
  const [splitProducts, setSplitProducts] = useState<SplitProductType[]>([])
  const [currentSelectedProductUniqueId, setCurrentSelectedProductUniqueId] =
    useState<string>()

  // useEffect
  useEffect(() => {
    redirectFromPayment()
  }, [tableStatus, isTableDataLoaded, isTableLoading])

  useEffect(() => {
    checkIfLoyaltyCardExists()
  }, [isUserAuth])

  // Consts
  const cloudItemsGroupped: ITableCloudItemGroupped[] = useMemo(() => {
    return cloudItems?.reduce(
      (acc: ITableCloudItemGroupped[], curr: ITableCloudItem) => {
        const sMI = JSON.parse(curr.menuItems || '[]')
          ?.map((sMI: any) => `${sMI.productId}_${sMI.count}`)
          ?.sort()
          ?.toString()

        const sKI = Object.values(JSON.parse(curr.kitchenFreeInfo || '{}'))
          ?.filter(Boolean)
          ?.sort()
          ?.join('')

        const uniqueId = `${curr.productId}_${sKI}_${sMI}_${curr.discountP}`
        const foundIndex = acc.findIndex((item) => item.uniqueId === uniqueId)

        if (foundIndex !== -1) {
          acc[foundIndex] = {
            ...acc?.[foundIndex],
            itemGuids: [
              ...(acc?.[foundIndex]?.itemGuids || []),
              curr?.itemGuid,
            ],
            count: (acc?.[foundIndex]?.count || 0) + (curr?.count || 0),
          }
        } else {
          acc.push({
            ...curr,
            uniqueId,
            itemGuids: [curr.itemGuid],
          })
        }

        return acc
      },
      []
    )
  }, [cloudItems])

  // Functions
  const handleSplitProductClick = (
    _productId: any,
    _e: any,
    clickedProduct: ICartProduct
  ) => {
    setSplitProducts((prevValue) => {
      if (!prevValue?.find((el) => el.uniqueId === clickedProduct.uniqueId)) {
        setCurrentSelectedProductUniqueId(clickedProduct.uniqueId)
        return [
          ...prevValue,
          {
            uniqueId: clickedProduct.uniqueId || '',
            count: clickedProduct.count,
          },
        ]
      } else {
        setCurrentSelectedProductUniqueId(undefined)
        return prevValue?.filter(
          (el) => el.uniqueId !== clickedProduct.uniqueId
        )
      }
    })
  }

  const getIsProductSelectedForSplit = (product: ITableCloudItem) =>
    !!splitProducts?.find((el) => el.uniqueId === product.uniqueId)?.count

  const getResultPrice = (): number => {
    if (currentPage === 'split' || splitProducts?.length) {
      const total = splitProducts?.reduce((acc, curr) => {
        const cloudItem = cloudItemsGroupped?.find(
          (cItem) => cItem.uniqueId === curr.uniqueId
        )
        if (cloudItem) {
          const productTotal = getProductTotalPrice(
            {
              ...cloudItem,
              count: curr.count,
            },
            currency
          )
          return (acc += productTotal)
        }
        return acc
      }, 0)
      return total
    } else {
      return cloudTotal
    }
  }

  const getResultPriceWithCurrency = () =>
    `${convertToPrice(getResultPrice() + tipsValue)} ${currency || ''}`

  const getProductSplitCount = (product: ITableCloudItem) =>
    splitProducts?.find((el) => el.uniqueId === product.uniqueId)?.count || 0

  const handleSplitCount = (newValue: number) => {
    if (currentSelectedProductUniqueId) {
      setSplitProducts((prevValue) => {
        if (newValue) {
          return prevValue?.map((el) => {
            if (el.uniqueId === currentSelectedProductUniqueId) {
              return { ...el, count: newValue }
            } else {
              return el
            }
          })
        } else {
          return prevValue?.filter(
            (el) => el.uniqueId !== currentSelectedProductUniqueId
          )
        }
      })
    }
  }

  const getIsNextAndPayDisabled = () => {
    return (
      (!isUserAuth && !isAVGConfirmed) ||
      (currentPage === 'split' && !splitProducts?.length)
    )
  }

  const checkIfLoyaltyCardExists = async () => {
    try {
      if (isUserAuth) {
        const res = await clickCollectGET.customerCard()
        const total = Number(res?.total)
        setLoyaltyCardBalance(!isNaN(total) ? total : 0)
      }
    } catch (e) {
      errorHandler(e)
    }
  }

  const openAgeProtectionModal = (
    itemsToPay: ITableCloudItem[] | IBucketProduct[],
    isHighlyProtected = false
  ) => {
    openModal({
      id: 'AGE_PROTECTION_MODAL',
      title: t('common.confirmYourAge'),
      components: [
        ({ closeThisModal }) => (
          <AgeProtectionModal
            isHighlyProtected={isHighlyProtected}
            startPayment={() => {
              startPaymentRequest(itemsToPay)
              closeThisModal()
            }}
          />
        ),
      ],
    })
  }

  const startPaymentProccess = () => {
    if (!disabledByMW) {
      const itemsToPay: ITableCloudItem[] | IBucketProduct[] = (function () {
        switch (true) {
          case Boolean(splitProducts?.length):
            return splitProducts?.map((splitP) => {
              const fullItem = cloudItemsGroupped?.find(
                (cloudP) => cloudP.uniqueId === splitP.uniqueId
              )
              if (fullItem) {
                return {
                  ...fullItem,
                  itemGuids: fullItem.itemGuids.slice(0, splitP.count),
                  count: splitP.count,
                }
              }
              return splitP as ITableCloudItem
            })
          default:
            return cloudItems
        }
      })()
      if (
        itemsToPay?.some((product) =>
          isProductHighlyProtected(product.productId)
        )
      ) {
        openAgeProtectionModal(itemsToPay, true)
      } else if (
        itemsToPay?.some((product) => isProductProtected(product.productId))
      ) {
        openAgeProtectionModal(itemsToPay)
      } else {
        startPaymentRequest(itemsToPay)
      }
    } else {
      openCallWaiterModal(true)
    }
  }

  const startPaymentRequest = async (
    itemsToPay: ITableCloudItem[] | IBucketProduct[]
  ) => {
    if (isAVGConfirmed || isUserAuth) {
      try {
        setIsLoading(true)
        await qrMenuGET.modVer(modVer, orderGuid)

        try {
          if (!isUserAuth) {
            await startPayment(itemsToPay, getResultPrice(), tipsValue)
          }
          if (isUserAuth) {
            const currentUserInfo = await Auth.currentUserInfo()
            const attributes = currentUserInfo?.attributes
            if (
              (!attributes || !attributes?.['custom:active_card_token']) &&
              !loyaltyCardBalance
            ) {
              await startPayment(itemsToPay, getResultPrice(), tipsValue)
            } else {
              setIsLoading(false)
              openModal({
                id: 'PAYMENT_CARDS_MODAL',
                // TODO: translations
                title: t('order.choosePaymentVariant'),
                components: [
                  ({ closeThisModal }) => (
                    <CardsModal
                      isLoyaltyCardExisting={!!loyaltyCardBalance}
                      isLoyaltyCardDisabled={
                        loyaltyCardBalance < getResultPrice() + tipsValue
                      }
                      itemsToPay={itemsToPay}
                      tipsValue={tipsValue}
                      userCardData={attributes?.['custom:active_card_token']}
                      closeModal={closeThisModal}
                      total={getResultPrice()}
                    />
                  ),
                ],
              })
            }
          }
        } catch (eee) {
          errorHandler(eee)
        }
      } catch (e) {
        console.log('mod ver error:', e)
        // TODO: location.reload is temporary and fastest solution. get rid of it
        window.location.reload() // mod ver is wrong. we need to reload to get refreshed data
      } finally {
        setIsLoading(false)
      }
    }
  }

  const redirectFromPayment = () => {
    if (paymentVariant === 1 && tableStatus !== TableStatuses.FREE) {
      navigate(getNavLink(searchParams, ''))
    }
    if (paymentVariant === 2 && isTableDataLoaded && !isTableLoading) {
      openEmptyBasketModal(tableStatus)
    }
  }

  const openEmptyBasketModal = (tStatus: TableStatuses) => {
    if (tStatus === TableStatuses.UNDEFINED) {
      navigate(getNavLink(searchParams, ''))
      dispatch(
        openToast({
          message: t('errors.smthWentWrong'),
          type: 'error',
        })
      )
    }
    if (tStatus === TableStatuses.FREE) {
      openModal({
        id: 'BUSKET_IS_EMPTY',
        title: '',
        shouldHideCross: true,
        components: [
          ({ closeThisModal }) => (
            <ConfirmationModal
              onConfirm={() => {
                navigate(getNavLink(searchParams, ''))
                closeThisModal()
              }}
              text={t('errors.basketIsEmpty')}
            />
          ),
        ],
      })
    }
    if (tStatus === TableStatuses.BUSY) {
      openModal({
        id: 'TABLE_IS_BUSY',
        title: '',
        shouldHideCross: true,
        components: [
          ({ closeThisModal }) => (
            <ConfirmationModal
              onConfirm={() => {
                navigate(getNavLink(searchParams, ''))
                closeThisModal()
              }}
              text={t('errors.tableIsLocked')}
            />
          ),
        ],
      })
    }
  }

  // Components
  const renderAmountHandler = () => (
    <AmountHandler
      isSmall
      currentValue={
        splitProducts?.find(
          (el) => el.uniqueId === currentSelectedProductUniqueId
        )?.count || 0
      }
      minValue={0}
      maxValue={
        cloudItemsGroupped?.find(
          (el) => el.uniqueId === currentSelectedProductUniqueId
        )?.count
      }
      onDecrease={handleSplitCount}
      onIncrease={handleSplitCount}
    />
  )

  return (
    <>
      <PaymentContainer>
        <Header />
        <EmptyBlock />
        <RenderIf
          condition={isTableLoading || !isProductsLoaded || !isTableDataLoaded}
        >
          <Spinner type="transparent" />
        </RenderIf>

        <RenderIf condition={!isProductsLoaded || !cloudItems?.length}>
          <FakeCart />
        </RenderIf>

        {/* CART */}
        <RenderIf
          condition={
            currentPage === 'cart' && isProductsLoaded && !!cloudItems?.length
          }
        >
          <Cart products={cloudItemsGroupped || cloudItems} isBucket />
        </RenderIf>

        {/* SPLIT */}
        <RenderIf condition={currentPage === 'split'}>
          <Cart
            isSplit
            isBucket
            products={cloudItemsGroupped}
            getAltCount={getProductSplitCount}
            handleProductClick={handleSplitProductClick}
            getIsProductSelected={getIsProductSelectedForSplit}
          />
        </RenderIf>

        {/* TIPS */}
        <RenderIf condition={currentPage === 'tips'}>
          <Tips
            // total={cloudTotal}
            total={getResultPrice()}
            tipsValue={tipsValue}
            onChange={setTipsValue}
          />
        </RenderIf>
        {/* SUBFOOTER */}
        <SubFooter
          isAVGConfirmed={isAVGConfirmed}
          setIsAVGConfirmed={setIsAVGConfirmed}
          //
          isUserAuth={isUserAuth}
        />
        <EmptyBlock height="120px" />
      </PaymentContainer>

      {/* FOOTER */}
      <FooterPaymentQRM
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        renderAmountHandler={renderAmountHandler}
        getIsNextAndPayDisabled={getIsNextAndPayDisabled}
        tipsValue={tipsValue}
        startPaymentProccess={startPaymentProccess}
        isLoading={isLoading}
        getResultPrice={getResultPriceWithCurrency}
      />
    </>
  )
}
